// @flow

import React, { useState, useEffect, useRef, type Element } from 'react'
import { merge } from 'lodash'
import { useForm, FormProvider } from 'react-hook-form'
import { Container, Grid } from '@material-ui/core'
import { ThemeProvider } from '@material-ui/core/styles'

import theme from '../../../theme'
import CustomStepper from '../../Common/CustomStepper'
import DetailsForm from '../DetailsForm'
import { formSections } from '../DetailsForm/fields.js'
import ClaimsList from '../ClaimsList'
import NoEligibleClaims from '../NoEligibleClaims'

import type { Claim, Patient, User, FormData } from '../Types'

const steps = ['Select claims', 'Invoice details']

type Props = { records: Array<Claim>, province: string, user: User, patient: Patient }

function PrivateInvoiceContainer({
  records,
  province,
  user,
  patient,
}: $ReadOnly<Props>): Element<typeof ThemeProvider> {
  const formDefaults: FormData = {
    user: {
      name: user.name,
      address1: '',
      address2: '',
      city: '',
      province: '',
      postalCode: '',
      email: '',
      phone: '',
    },
    patient: {
      name: patient.name,
      address1: patient.address,
      address2: '',
      city: patient.city,
      province: patient.province,
      postalCode: patient.postalCode,
      country: patient.country,
    },
    invoiceDetails: {
      number: '',
      terms:
        'Payment is due 30 days after invoice date. Payment method is below. See payment stub for details.',
      date: new Date().getTime(),
    },
    claims: [],
  }

  const [activeStep, setActiveStep] = useState(0)
  const [submitFormData, setSubmitFormData] = useState(false)
  const [formData, setFormData] = useState<FormData>(formDefaults)
  const [claims, setClaims] = useState(records)
  const [selectedClaims, setSelectedClaims] = useState([])
  const useFormMethods = useForm<FormData>({ mode: 'onTouched', defaultValues: formData })

  function handleNext() {
    if (activeStep === steps.length) {
      return
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  function handleBack() {
    const values = useFormMethods.getValues()
    setFormData(merge(formData, values))

    if (activeStep === 0) {
      return
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  function handleCancel() {
    window.history.back()
  }

  function isLastStep(): boolean {
    return activeStep >= steps.length - 1
  }

  const handleErrors = (errors, e) => {
    alert('Invalid input. Please fix errors before resubmitting.')
    setSubmitFormData(false)
  }

  const handleOnSubmit = (data, e) => {
    const values = useFormMethods.getValues()
    const allFormData = { ...values, ...{ claims: selectedClaims } }
    const submittedData = merge(formData, allFormData)
    setSubmitFormData(true)
    setFormData(submittedData)
  }

  const hiddenPrivateInvoiceForm = useRef(null)
  useEffect(() => {
    if (submitFormData) {
      if (hiddenPrivateInvoiceForm.current instanceof HTMLFormElement) {
        hiddenPrivateInvoiceForm.current.submit()
      }
    }
  }, [submitFormData])

  const noEligibleClaims = records.length === 0

  function renderForm(activeStep) {
    if (noEligibleClaims) {
      return <NoEligibleClaims patientId={patient.id} handleCancel={handleCancel} />
    } else {
      switch (activeStep) {
        case 0:
          return (
            <ClaimsList
              records={claims}
              onEditingClaims={onEditingClaims}
              province={province}
              handleCancel={handleCancel}
              handleNext={handleNext}
              onSelectedCallback={onSelectedCallback}
            />
          )
        case 1:
          return (
            <FormProvider {...useFormMethods}>
              <DetailsForm
                formSections={formSections}
                handleOnSubmit={handleOnSubmit}
                handleErrors={handleErrors}
                handleCancel={handleCancel}
                handleBack={handleBack}
              />
            </FormProvider>
          )
        default:
          return <p>UNEXPECTED ERROR!</p>
      }
    }
  }

  function onSelectedCallback(records: Array<Claim>): void {
    setSelectedClaims(records)
  }

  function onEditingClaims(records: Array<Claim>): void {
    setClaims(records)
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <CustomStepper activeStep={activeStep} steps={steps} />
      </Container>
      <Container maxWidth={isLastStep() ? 'sm' : 'md'}>
        <Grid container justify="center" spacing={3}>
          <form
            id="hidden-private-invoice-form"
            ref={hiddenPrivateInvoiceForm}
            action={`/patients/${patient.id}/printable_invoice`}
            method="POST"
            target="_blank"
          >
            <input
              id="private-invoice-form-params"
              type="hidden"
              name="invoice"
              value={JSON.stringify(formData)}
            />
          </form>
          <Grid item>{renderForm(activeStep)}</Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  )
}

export default PrivateInvoiceContainer
